import { createBrowserRouter, Navigate } from "react-router-dom"
import GeneralError from "./pages/Errors/GeneralError"
import NoAccess from "./pages/Errors/NoAcces"
import NotFoundError from "./pages/Errors/NotFound"
import ProtectedRoute from "./routers/ProtectedRoute"
import { WithSurveyColumns } from "./sections/PerceptionReport/tables/TeamLeaderSummary/TeamLeaderSummary.api"

const router = createBrowserRouter([
  // Auth Routes
  {
    path: "/login",
    lazy: async () => ({
      Component: (await import("./pages/Login/Login")).default,
    }),
  },
  {
    path: "logout",
    lazy: async () => ({
      Component: (await import("./pages/Logout/Logout")).default,
    }),
  },

  // Root Route (Redirect to BeYouCoach Dashboard)
  {
    path: "/",
    element: <Navigate to="/dashboard" replace />,
  },

  // Main Application Routes
  {
    path: "/",
    lazy: async () => {
      const Layout = await import("./components/Layout/Layout")
      return {
        Component: (props) => {
          return (
            <ProtectedRoute>
              <Layout.default {...props} />
            </ProtectedRoute>
          )
        },
      }
    },
    // errorElement: <GeneralError />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" />,
      },

      {
        path: "dashboard",
        lazy: async () => ({
          Component: (await import("./pages/Dashboard/Dashboard")).default,
        }),
      },
      {
        path: "badges",
        lazy: async () => ({
          Component: (await import("./pages/Badges/Badges")).default,
        }),
      },
      {
        path: "insights",
        lazy: async () => ({
          Component: (await import("./sections/Insights/Insights")).default,
        }),
      },
      {
        path: "vouchers",
        lazy: async () => ({
          Component: (await import("./pages/Vouchers/Vouchers")).default,
        }),
      },

      {
        path: "performance",
        children: [
          {
            path: "my-performance",
            lazy: async () => ({
              Component: (await import("./pages/Performance/Performance")).default,
            }),
          },
          {
            path: "my-performance/:entity_no",
            lazy: async () => ({
              Component: (await import("./pages/Performance/Performance")).default,
            }),
          },
          // {
          //   path: "production",
          //   lazy: async () => ({
          //     Component: (await import("./pages/Production/Production")).default,
          //   }),
          // },
        ],
      },
      {
        path: "profile",
        lazy: async () => ({
          Component: (await import("./pages/Profile/Profile")).default,
        }),
      },
      {
        path: "profile/:entity_no",
        lazy: async () => ({
          Component: (await import("./pages/Profile/Profile")).default,
        }),
      },
      {
        path: "friends",
        lazy: async () => ({
          Component: (await import("./sections/User/sections/FriendSearch/FriendSearch")).default,
        }),
      },
      {
        path: "challenge",
        lazy: async () => ({
          Component: (await import("./pages/Challenge/Challenge")).default,
        }),
      },
      {
        path: "inbox",
        lazy: async () => ({
          Component: (await import("./pages/Messaging/Messaging")).default,
        }),
      },
      {
        path: "leaderboard",
        lazy: async () => ({
          Component: (await import("./pages/Leaderboard/Leaderboard")).default,
        }),
      },
      {
        path: "reports",
        element: <ProtectedRoute requiredRoles={["reports_view"]} />,
        children: [
          {
            path: "perception/survey-responses",
            lazy: async () => {
              const { default: SurveyReport } = await import(
                "./sections/PerceptionReport/reports/SurveyReport/SurveyReport"
              )
              const WithProps = () => <SurveyReport key="mbr-survey-report" survey="mbr" columns={WithSurveyColumns} />
              return { Component: WithProps }
            },
          },
          {
            path: "perception/fcr",
            lazy: async () => {
              const { default: SurveyReport } = await import(
                "./sections/PerceptionReport/reports/SurveyReport/SurveyReport"
              )
              const WithProps = () => <SurveyReport key="mbr-survey-report" survey="fcr" columns={WithSurveyColumns} />
              return { Component: WithProps }
            },
          },
          {
            path: "perception/lsa",
            lazy: async () => ({
              Component: (await import("./sections/PerceptionReport/reports/LsaReport/LsaReport")).default,
            }),
          },
          {
            path: "perception/appeals",
            lazy: async () => ({
              Component: (await import("./sections/PerceptionReport/reports/AppealReport/AppealReport")).default,
            }),
          },
          {
            path: "usage",
            lazy: async () => ({
              Component: (await import("./sections/UsageReport/UsageReport")).default,
            }),
          },
          {
            path: "production",
            lazy: async () => ({
              Component: (await import("./sections/Production/sections/ProductionReport/ProductionReport")).default,
            }),
          },
          {
            path: "production/additional_volume",
            lazy: async () => ({
              Component: (await import("./sections/Production/sections/AdditionalVolumeReport/AdditionalVolumeReport"))
                .default,
            }),
          },
          {
            path: "twt-report",
            lazy: async () => ({
              Component: (await import("./sections/TWT/TWTReport")).default,
            }),
          },
        ],
      },
      {
        path: "admin",
        children: [
          {
            path: "incentive",
            lazy: async () => {
              const Module = await import("./sections/Metric/sections/MaintainIncentive/MaintainIncentive")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_incentives"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "periods",
            lazy: async () => {
              const Module = await import("./sections/Surveys/sections/MaintainPeriods/MaintainPeriods")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_incentives"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "prod-periods",
            lazy: async () => {
              const Module = await import("./sections/Surveys/sections/MaintainPeriods/MaintainProdPeriods")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_incentives"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "user",
            lazy: async () => {
              const Module = await import("./sections/MaintainUser/MaintainUser")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "access",
            lazy: async () => {
              const Module = await import("./sections/Access/components/MaintainAccess/MaintainAccess")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_access"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "twt-teams",
            lazy: async () => {
              const Module = await import("./sections/TWT/MaintainTWTteams")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_production"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "prod-pool",
            lazy: async () => {
              const Module = await import("./sections/Production/ProductionPoolWeight")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_production"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "appeals",
            lazy: async () => {
              const Module = await import("./sections/Appeals/components/MaintainAppeals/MaintainAppeals")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "reports_view"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "production",
            lazy: async () => {
              const Module = await import("./sections/Production/sections/MaintainProduction/MaintainProduction")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_production"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "twt",
            lazy: async () => {
              const Module = await import("./sections/TWT/MaintainTWTgroups")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_production"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "public-holidays",
            lazy: async () => {
              const Module = await import("./sections/Production/sections/MaintainHolidays/MaintainHolidays")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_production"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "help-files",
            lazy: async () => {
              const Module = await import("./sections/HelpFiles/sections/MaintainHelpFiles/MaintainHelpFiles")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_content"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "data-file-upload",
            lazy: async () => {
              const Module = await import("./pages/FileUpload/FileUpload")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["admin_view", "admin_production"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
        ],
      },
      {
        path: "coaching-forms",

        children: [
          {
            path: "submitted",
            lazy: async () => ({
              Component: (await import("./pages/CoachingForms/SubmittedForms")).default,
            }),
          },
          {
            path: "templates",
            lazy: async () => {
              const Module = await import("./pages/CoachingForms/CoachingForms")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["team_manage"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
          {
            path: "team-development",
            lazy: async () => {
              const Module = await import("./sections/Ovation/NegativeOvation")
              const LoadedComponent = Module.default

              return {
                Component: () => (
                  <ProtectedRoute requiredRoles={["team_manage"]}>
                    <LoadedComponent />
                  </ProtectedRoute>
                ),
              }
            },
          },
        ],
      },
      {
        path: "ovation",

        children: [
          {
            path: "report",
            lazy: async () => ({
              Component: (await import("./sections/Ovation/Ovation")).default,
            }),
          },
          {
            path: "ovation-leaderboard",
            lazy: async () => ({
              Component: (await import("./sections/Ovation/OvationLeaderboard")).default,
            }),
          },
        ],
      },
      {
        path: "search",
        lazy: async () => ({
          Component: (await import("./pages/GlobalSearch/GlobalSearch")).default,
        }),
      },
      {
        path: "help",
        lazy: async () => ({
          Component: (await import("./pages/Help/Help")).default,
        }),
      },
      {
        path: "knowledge-base",
        lazy: async () => ({
          Component: (await import("./pages/KnowledgeBase/KnowledgeBase")).default,
        }),
      },
      {
        path: "training/:id",
        lazy: async () => ({
          Component: (await import("./sections/KnowledgeBase/TrainingDetail")).default,
        }),
      },
      {
        path: "general/:id",
        lazy: async () => ({
          Component: (await import("./sections/KnowledgeBase/GenericPage")).default,
        }),
      },
    ],
  },

  // Error routes
  { path: "/500", Component: GeneralError },
  { path: "/404", Component: NotFoundError },
  { path: "/no-access", Component: NoAccess },

  // Fallback 404 route
  { path: "*", Component: NotFoundError },
])

export default router
